import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { AlertCircle, User, Mail, ArrowRight } from 'lucide-react';
import api from '../../utils/api';
import { setRegistrationDraft } from '../../utils/registrationSession';
import { formatRegistrationText } from '../../utils/textFormat';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import RegisterLayout from './RegisterLayout';

const schema = z.object({
  full_name: z.string().trim().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Enter a valid email'),
});

export default function RegisterAccountStep() {
  const navigate = useNavigate();
  const [serverError, setServerError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { full_name: '', email: '' },
  });

  const onSubmit = async (values) => {
    setServerError('');
    setSubmitting(true);
    const fullName = formatRegistrationText(values.full_name, 'first');
    const email = values.email.trim().toLowerCase();

    try {
      await api.post('auth/register/send-otp', { full_name: fullName, email });
      setRegistrationDraft({ email, fullName });
      navigate('/register/verify');
    } catch (e) {
      setServerError(e?.response?.data?.message || e?.message || 'Could not send verification code');
    } finally {
      setSubmitting(false);
    }
  };

  const firstError = serverError || errors.full_name?.message || errors.email?.message;

  return (
    <RegisterLayout
      step={1}
      title="Create account"
      description="Step 1 — Verify your email address"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {firstError && (
          <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {firstError}
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="full_name">Full Name</Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input id="full_name" className="pl-10" placeholder="Your full name" {...register('full_name', { setValueAs: (v) => (v || '').trim() })} />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input id="email" type="email" className="pl-10" placeholder="you@school.edu" {...register('email')} />
          </div>
        </div>

        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting ? 'Sending code…' : 'Continue'}
          {!submitting && <ArrowRight className="w-4 h-4 ml-2" />}
        </Button>
      </form>
    </RegisterLayout>
  );
}
